import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { API_ENDPOINTS } from '../../config/apiRouting';
import './ABMDashboard.css';

const BSO_STORE_ID = 'bso-hq-001';

interface CompanyList {
  listId: string;
  name: string;
  description: string;
  tier: string;
  segment: string;
  companyCount: number;
  linkedinSegmentUrn: string | null;
  status: string;
  createdAt: string;
}

interface Company {
  companyName: string;
  companyDomain: string;
  locationCount: number;
  knownPOS: string;
  notes: string;
}

interface CampaignStep {
  stepNumber: number;
  blogPostTitle: string;
  blogPostUrl?: string;
  headline: string;
  introText: string;
  imageUrl: string;
  duration: number;
  status: string;
  startedAt: string | null;
  completedAt: string | null;
  linkedinCampaignId?: string;
  category?: string;
  phase?: string;
}

interface Campaign {
  campaignId: string;
  name: string;
  companyListId: string;
  jobTitleFilter: string[];
  dailyBudget: number;
  status: string;
  currentStep: number;
  totalSteps: number;
  createdAt: string;
}

interface CampaignMetrics {
  totalImpressions: number;
  totalClicks: number;
  totalSpend: number;
  ctr: string;
  cpc: string;
  totalConversions?: number;
}

interface AccountEngagement {
  companyUrn: string;
  companyName?: string;
  engagementScore: number;
  impressions: number;
  clicks: number;
}

interface CampaignEvent {
  eventType: string;
  timestamp: string;
  fromStep?: number;
  toStep?: number;
}

type SubTab = 'lists' | 'campaigns' | 'wizard' | 'analytics';

export const ABMDashboard: React.FC = () => {
  const { getIdToken } = useAuth();
  const [subTab, setSubTab] = useState<SubTab>('lists');

  // Account Lists state
  const [lists, setLists] = useState<CompanyList[]>([]);
  const [selectedList, setSelectedList] = useState<CompanyList | null>(null);
  const [listCompanies, setListCompanies] = useState<Company[]>([]);
  const [showCreateList, setShowCreateList] = useState(false);
  const [listForm, setListForm] = useState({ name: '', description: '', tier: 'TIER_2', segment: 'QSR' });
  const [csvData, setCsvData] = useState('');
  const [listsLoading, setListsLoading] = useState(false);

  // Campaigns state
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [selectedCampaign, setSelectedCampaign] = useState<string | null>(null);
  const [campaignDetail, setCampaignDetail] = useState<{
    campaign: Campaign | null;
    steps: CampaignStep[];
    metrics: CampaignMetrics | null;
    stepMetrics: any[];
    accounts: AccountEngagement[];
    events: CampaignEvent[];
  }>({ campaign: null, steps: [], metrics: null, stepMetrics: [], accounts: [], events: [] });
  const [showCreateCampaign, setShowCreateCampaign] = useState(false);
  const [campaignForm, setCampaignForm] = useState({
    name: '', companyListId: '', dailyBudget: 100,
    jobTitleFilter: 'Owner, CEO, Founder, VP Operations, Director'
  });
  const [campaignSteps, setCampaignSteps] = useState<CampaignStep[]>([]);
  const [campaignsLoading, setCampaignsLoading] = useState(false);

  // Wizard state
  const [wizardStep, setWizardStep] = useState(1);
  const [wizardConfig, setWizardConfig] = useState({ tier: 'TIER_1', segment: 'QSR', stepCount: 6 });
  const [wizardBlogPosts, setWizardBlogPosts] = useState<{ title: string; url: string; category: string }[]>([
    { title: '', url: '', category: 'awareness' }
  ]);
  const [generatedSequence, setGeneratedSequence] = useState<CampaignStep[]>([]);
  const [wizardLoading, setWizardLoading] = useState(false);
  const [wizardCampaignName, setWizardCampaignName] = useState('');
  const [wizardListId, setWizardListId] = useState('');
  const [wizardBudget, setWizardBudget] = useState(100);

  // Analytics state
  const [analyticsCampaignId, setAnalyticsCampaignId] = useState('');

  const [actionLoading, setActionLoading] = useState(false);

  const authHeaders = useCallback(async () => {
    const token = await getIdToken();
    return { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
  }, [getIdToken]);

  // Fetch company lists
  const fetchLists = useCallback(async () => {
    setListsLoading(true);
    try {
      const headers = await authHeaders();
      const resp = await fetch(API_ENDPOINTS.abmCompanyLists(), { headers });
      const data = await resp.json();
      if (data.success) setLists(data.lists || []);
    } catch (err) {
      console.error('Failed to fetch company lists:', err);
    }
    setListsLoading(false);
  }, [authHeaders]);

  // Fetch campaigns
  const fetchCampaigns = useCallback(async () => {
    setCampaignsLoading(true);
    try {
      const headers = await authHeaders();
      const resp = await fetch(API_ENDPOINTS.abmCampaigns(), { headers });
      const data = await resp.json();
      if (data.success) setCampaigns(data.campaigns || []);
    } catch (err) {
      console.error('Failed to fetch campaigns:', err);
    }
    setCampaignsLoading(false);
  }, [authHeaders]);

  // Fetch campaign detail
  const fetchCampaignDetail = useCallback(async (campaignId: string) => {
    try {
      const headers = await authHeaders();
      const resp = await fetch(API_ENDPOINTS.abmCampaign(campaignId), { headers });
      const data = await resp.json();
      if (data.success) {
        setCampaignDetail({
          campaign: data.campaign,
          steps: data.steps || [],
          metrics: data.metrics || null,
          stepMetrics: data.stepMetrics || [],
          accounts: data.accounts || [],
          events: data.events || []
        });
      }
    } catch (err) {
      console.error('Failed to fetch campaign detail:', err);
    }
  }, [authHeaders]);

  // Fetch list detail
  const fetchListDetail = useCallback(async (listId: string) => {
    try {
      const headers = await authHeaders();
      const resp = await fetch(API_ENDPOINTS.abmCompanyList(listId), { headers });
      const data = await resp.json();
      if (data.success) {
        setSelectedList(data.list);
        setListCompanies(data.companies || []);
      }
    } catch (err) {
      console.error('Failed to fetch list detail:', err);
    }
  }, [authHeaders]);

  useEffect(() => {
    if (subTab === 'lists') fetchLists();
    if (subTab === 'campaigns' || subTab === 'wizard' || subTab === 'analytics') {
      fetchCampaigns();
      fetchLists();
    }
  }, [subTab, fetchLists, fetchCampaigns]);

  useEffect(() => {
    if (selectedCampaign) fetchCampaignDetail(selectedCampaign);
  }, [selectedCampaign, fetchCampaignDetail]);

  // Create list
  const handleCreateList = async () => {
    setActionLoading(true);
    try {
      const headers = await authHeaders();
      const resp = await fetch(API_ENDPOINTS.abmCompanyLists(), {
        method: 'POST', headers,
        body: JSON.stringify({ ...listForm, storeId: BSO_STORE_ID })
      });
      const data = await resp.json();
      if (data.success) {
        setShowCreateList(false);
        setListForm({ name: '', description: '', tier: 'TIER_2', segment: 'QSR' });
        fetchLists();
      }
    } catch (err) {
      console.error('Failed to create list:', err);
    }
    setActionLoading(false);
  };

  // Import CSV
  const handleImportCsv = async () => {
    if (!selectedList || !csvData.trim()) return;
    setActionLoading(true);
    try {
      const headers = await authHeaders();
      const resp = await fetch(API_ENDPOINTS.abmCompanyListImport(selectedList.listId), {
        method: 'POST', headers,
        body: JSON.stringify({ csvData })
      });
      const data = await resp.json();
      if (data.success) {
        setCsvData('');
        fetchListDetail(selectedList.listId);
      }
    } catch (err) {
      console.error('Failed to import CSV:', err);
    }
    setActionLoading(false);
  };

  // Sync to LinkedIn
  const handleSyncList = async (listId: string) => {
    setActionLoading(true);
    try {
      const headers = await authHeaders();
      const resp = await fetch(API_ENDPOINTS.abmCompanyListSync(listId), {
        method: 'POST', headers,
        body: JSON.stringify({ storeId: BSO_STORE_ID })
      });
      const data = await resp.json();
      if (data.success) fetchLists();
    } catch (err) {
      console.error('Failed to sync list:', err);
    }
    setActionLoading(false);
  };

  // Delete list
  const handleDeleteList = async (listId: string) => {
    if (!window.confirm('Delete this company list?')) return;
    setActionLoading(true);
    try {
      const headers = await authHeaders();
      await fetch(API_ENDPOINTS.abmCompanyList(listId), {
        method: 'DELETE', headers,
        body: JSON.stringify({ storeId: BSO_STORE_ID })
      });
      setSelectedList(null);
      fetchLists();
    } catch (err) {
      console.error('Failed to delete list:', err);
    }
    setActionLoading(false);
  };

  // Create campaign
  const handleCreateCampaign = async () => {
    setActionLoading(true);
    try {
      const headers = await authHeaders();
      const resp = await fetch(API_ENDPOINTS.abmCampaigns(), {
        method: 'POST', headers,
        body: JSON.stringify({
          name: campaignForm.name,
          companyListId: campaignForm.companyListId,
          dailyBudget: campaignForm.dailyBudget,
          jobTitleFilter: campaignForm.jobTitleFilter.split(',').map(t => t.trim()),
          steps: campaignSteps,
          storeId: BSO_STORE_ID
        })
      });
      const data = await resp.json();
      if (data.success) {
        setShowCreateCampaign(false);
        setCampaignForm({ name: '', companyListId: '', dailyBudget: 100, jobTitleFilter: 'Owner, CEO, Founder, VP Operations, Director' });
        setCampaignSteps([]);
        fetchCampaigns();
      }
    } catch (err) {
      console.error('Failed to create campaign:', err);
    }
    setActionLoading(false);
  };

  // Campaign actions
  const handleCampaignAction = async (campaignId: string, action: string) => {
    if (action === 'cancel' && !window.confirm('Cancel this campaign?')) return;
    setActionLoading(true);
    try {
      const headers = await authHeaders();
      await fetch(API_ENDPOINTS.abmCampaign(campaignId), {
        method: 'PATCH', headers,
        body: JSON.stringify({ action, storeId: BSO_STORE_ID })
      });
      fetchCampaigns();
      if (selectedCampaign === campaignId) fetchCampaignDetail(campaignId);
    } catch (err) {
      console.error('Campaign action failed:', err);
    }
    setActionLoading(false);
  };

  // Publish campaign
  const handlePublishCampaign = async (campaignId: string) => {
    if (!window.confirm('Publish this campaign to LinkedIn?')) return;
    setActionLoading(true);
    try {
      const headers = await authHeaders();
      const resp = await fetch(API_ENDPOINTS.abmCampaignPublish(campaignId), {
        method: 'POST', headers,
        body: JSON.stringify({ storeId: BSO_STORE_ID })
      });
      const data = await resp.json();
      if (data.success) {
        fetchCampaigns();
        fetchCampaignDetail(campaignId);
      }
    } catch (err) {
      console.error('Failed to publish campaign:', err);
    }
    setActionLoading(false);
  };

  // Generate AI sequence
  const handleGenerateSequence = async () => {
    setWizardLoading(true);
    try {
      const headers = await authHeaders();
      const validPosts = wizardBlogPosts.filter(bp => bp.title.trim());
      const resp = await fetch(API_ENDPOINTS.abmGenerateSequence(), {
        method: 'POST', headers,
        body: JSON.stringify({
          tier: wizardConfig.tier,
          segment: wizardConfig.segment,
          stepCount: wizardConfig.stepCount,
          blogPosts: validPosts.length > 0 ? validPosts : undefined
        })
      });
      const data = await resp.json();
      if (data.success && data.sequence?.steps) {
        setGeneratedSequence(data.sequence.steps);
        setWizardStep(4);
      }
    } catch (err) {
      console.error('Failed to generate sequence:', err);
    }
    setWizardLoading(false);
  };

  // Create campaign from wizard
  const handleWizardCreateCampaign = async () => {
    if (!wizardCampaignName || !wizardListId) return;
    setActionLoading(true);
    try {
      const headers = await authHeaders();
      const resp = await fetch(API_ENDPOINTS.abmCampaigns(), {
        method: 'POST', headers,
        body: JSON.stringify({
          name: wizardCampaignName,
          companyListId: wizardListId,
          dailyBudget: wizardBudget,
          jobTitleFilter: ['Owner', 'CEO', 'Founder', 'VP Operations', 'Director'],
          steps: generatedSequence.map(s => ({
            blogPostTitle: s.blogPostTitle,
            headline: s.headline,
            introText: s.introText,
            duration: s.duration,
            imageUrl: s.imageUrl || ''
          })),
          storeId: BSO_STORE_ID
        })
      });
      const data = await resp.json();
      if (data.success) {
        setWizardStep(1);
        setGeneratedSequence([]);
        setWizardCampaignName('');
        setSubTab('campaigns');
        fetchCampaigns();
      }
    } catch (err) {
      console.error('Failed to create campaign from wizard:', err);
    }
    setActionLoading(false);
  };

  const tierBadge = (tier: string) => {
    const cls = tier === 'TIER_1' ? 'abm-badge-tier1' : tier === 'TIER_2' ? 'abm-badge-tier2' : 'abm-badge-tier3';
    return <span className={`abm-badge ${cls}`}>{tier.replace('_', ' ')}</span>;
  };

  const statusBadge = (status: string) => {
    const cls = `abm-badge-${status.toLowerCase()}`;
    return <span className={`abm-badge ${cls}`}>{status}</span>;
  };

  // =============================================
  // RENDER: Account Lists Tab
  // =============================================
  const renderListsTab = () => {
    if (selectedList) {
      return (
        <div>
          <div className="abm-detail-header">
            <h3>{selectedList.name} {tierBadge(selectedList.tier)} {statusBadge(selectedList.status)}</h3>
            <span className="abm-back-link" onClick={() => { setSelectedList(null); setListCompanies([]); }}>Back to Lists</span>
          </div>
          <p style={{ color: '#64748b', marginBottom: 16 }}>{selectedList.description || 'No description'}</p>
          <div className="abm-metrics-grid">
            <div className="abm-metric-card"><div className="metric-label">Companies</div><div className="metric-value">{selectedList.companyCount}</div></div>
            <div className="abm-metric-card"><div className="metric-label">Segment</div><div className="metric-value">{selectedList.segment}</div></div>
            <div className="abm-metric-card"><div className="metric-label">LinkedIn Synced</div><div className="metric-value">{selectedList.linkedinSegmentUrn ? 'Yes' : 'No'}</div></div>
          </div>

          <div className="abm-btn-group" style={{ marginBottom: 16 }}>
            <button className="abm-btn abm-btn-success" onClick={() => handleSyncList(selectedList.listId)} disabled={actionLoading}>
              Sync to LinkedIn
            </button>
            <button className="abm-btn abm-btn-danger" onClick={() => handleDeleteList(selectedList.listId)} disabled={actionLoading}>
              Delete List
            </button>
          </div>

          {/* Company table */}
          <div className="abm-card">
            <h3>Companies ({listCompanies.length})</h3>
            {listCompanies.length > 0 ? (
              <table className="abm-table">
                <thead><tr><th>Company</th><th>Domain</th><th>Locations</th><th>Known POS</th><th>Notes</th></tr></thead>
                <tbody>
                  {listCompanies.map((c, i) => (
                    <tr key={i}>
                      <td>{c.companyName}</td>
                      <td>{c.companyDomain}</td>
                      <td>{c.locationCount}</td>
                      <td>{c.knownPOS || '-'}</td>
                      <td>{c.notes || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="abm-empty">No companies added yet. Use CSV import below.</p>
            )}
          </div>

          {/* CSV Import */}
          <div className="abm-card">
            <h3>CSV Import</h3>
            <p style={{ fontSize: 13, color: '#64748b', marginBottom: 8 }}>
              Format: name, domain, locations, pos, notes (one per line, header row required)
            </p>
            <textarea
              className="abm-textarea"
              placeholder="name,domain,locations,pos,notes&#10;McDonald's,mcdonalds.com,38000,Custom,Global QSR leader&#10;Wendy's,wendys.com,7000,NCR Aloha,Fast casual chain"
              value={csvData}
              onChange={e => setCsvData(e.target.value)}
            />
            <div className="abm-btn-group">
              <button className="abm-btn abm-btn-primary" onClick={handleImportCsv} disabled={!csvData.trim() || actionLoading}>
                Import Companies
              </button>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3 style={{ margin: 0 }}>Account Lists</h3>
          <button className="abm-btn abm-btn-primary" onClick={() => setShowCreateList(true)}>Create List</button>
        </div>

        {showCreateList && (
          <div className="abm-card">
            <h3>New Company List</h3>
            <div className="abm-form-row">
              <div className="abm-form-group">
                <label>Name</label>
                <input className="abm-input" value={listForm.name} onChange={e => setListForm({ ...listForm, name: e.target.value })} placeholder="Enterprise QSR Targets" />
              </div>
              <div className="abm-form-group">
                <label>Tier</label>
                <select className="abm-select" value={listForm.tier} onChange={e => setListForm({ ...listForm, tier: e.target.value })}>
                  <option value="TIER_1">Tier 1 - Strategic (50+ locations)</option>
                  <option value="TIER_2">Tier 2 - Growth (10-50 locations)</option>
                  <option value="TIER_3">Tier 3 - Market (3-10 locations)</option>
                </select>
              </div>
            </div>
            <div className="abm-form-row">
              <div className="abm-form-group">
                <label>Segment</label>
                <select className="abm-select" value={listForm.segment} onChange={e => setListForm({ ...listForm, segment: e.target.value })}>
                  <option value="QSR">QSR (Quick Service)</option>
                  <option value="FAST_CASUAL">Fast Casual</option>
                  <option value="FULL_SERVICE">Full Service</option>
                  <option value="PIZZA">Pizza</option>
                  <option value="COFFEE_BAKERY">Coffee & Bakery</option>
                </select>
              </div>
              <div className="abm-form-group">
                <label>Description</label>
                <input className="abm-input" value={listForm.description} onChange={e => setListForm({ ...listForm, description: e.target.value })} placeholder="Optional description" />
              </div>
            </div>
            <div className="abm-btn-group">
              <button className="abm-btn abm-btn-primary" onClick={handleCreateList} disabled={!listForm.name || actionLoading}>Create</button>
              <button className="abm-btn abm-btn-secondary" onClick={() => setShowCreateList(false)}>Cancel</button>
            </div>
          </div>
        )}

        {listsLoading ? (
          <div className="abm-loading">Loading lists...</div>
        ) : lists.length === 0 ? (
          <div className="abm-empty">No company lists yet. Create one to get started.</div>
        ) : (
          <table className="abm-table">
            <thead><tr><th>Name</th><th>Tier</th><th>Segment</th><th>Companies</th><th>Status</th><th>Actions</th></tr></thead>
            <tbody>
              {lists.map(list => (
                <tr key={list.listId}>
                  <td><span style={{ cursor: 'pointer', color: '#2563eb' }} onClick={() => fetchListDetail(list.listId)}>{list.name}</span></td>
                  <td>{tierBadge(list.tier)}</td>
                  <td>{list.segment}</td>
                  <td>{list.companyCount}</td>
                  <td>{statusBadge(list.status)}</td>
                  <td>
                    <div className="abm-btn-group" style={{ marginTop: 0 }}>
                      <button className="abm-btn abm-btn-secondary" style={{ padding: '4px 10px', fontSize: 12 }} onClick={() => handleSyncList(list.listId)} disabled={actionLoading}>Sync</button>
                      <button className="abm-btn abm-btn-danger" style={{ padding: '4px 10px', fontSize: 12 }} onClick={() => handleDeleteList(list.listId)} disabled={actionLoading}>Delete</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    );
  };

  // =============================================
  // RENDER: Campaigns Tab
  // =============================================
  const renderCampaignsTab = () => {
    if (selectedCampaign && campaignDetail.campaign) {
      const c = campaignDetail.campaign;
      const m = campaignDetail.metrics;
      return (
        <div>
          <div className="abm-detail-header">
            <h3>{c.name} {statusBadge(c.status)}</h3>
            <span className="abm-back-link" onClick={() => { setSelectedCampaign(null); setCampaignDetail({ campaign: null, steps: [], metrics: null, stepMetrics: [], accounts: [], events: [] }); }}>Back to Campaigns</span>
          </div>

          {/* Step timeline */}
          <div className="abm-step-timeline">
            {campaignDetail.steps.map((step, i) => (
              <React.Fragment key={i}>
                {i > 0 && <div className={`abm-step-line ${step.status === 'COMPLETED' || campaignDetail.steps[i - 1]?.status === 'COMPLETED' ? 'completed' : ''}`} />}
                <div className={`abm-step-dot ${step.status === 'COMPLETED' ? 'completed' : step.status === 'ACTIVE' ? 'active' : 'pending'}`}>
                  {step.stepNumber}
                </div>
              </React.Fragment>
            ))}
          </div>

          {/* Action buttons */}
          <div className="abm-btn-group" style={{ marginBottom: 20 }}>
            {c.status === 'DRAFT' && (
              <button className="abm-btn abm-btn-success" onClick={() => handlePublishCampaign(c.campaignId)} disabled={actionLoading}>Publish to LinkedIn</button>
            )}
            {c.status === 'ACTIVE' && (
              <button className="abm-btn abm-btn-secondary" onClick={() => handleCampaignAction(c.campaignId, 'pause')} disabled={actionLoading}>Pause</button>
            )}
            {c.status === 'PAUSED' && (
              <button className="abm-btn abm-btn-primary" onClick={() => handleCampaignAction(c.campaignId, 'resume')} disabled={actionLoading}>Resume</button>
            )}
            {(c.status === 'ACTIVE' || c.status === 'PAUSED') && (
              <button className="abm-btn abm-btn-danger" onClick={() => handleCampaignAction(c.campaignId, 'cancel')} disabled={actionLoading}>Cancel</button>
            )}
          </div>

          {/* Metrics */}
          {m && (
            <div className="abm-metrics-grid">
              <div className="abm-metric-card"><div className="metric-label">Impressions</div><div className="metric-value">{(m.totalImpressions || 0).toLocaleString()}</div></div>
              <div className="abm-metric-card"><div className="metric-label">Clicks</div><div className="metric-value">{(m.totalClicks || 0).toLocaleString()}</div></div>
              <div className="abm-metric-card"><div className="metric-label">CTR</div><div className="metric-value">{m.ctr || '0.00'}%</div></div>
              <div className="abm-metric-card"><div className="metric-label">Spend</div><div className="metric-value">${(m.totalSpend || 0).toFixed(2)}</div></div>
              <div className="abm-metric-card"><div className="metric-label">CPC</div><div className="metric-value">${m.cpc || '0.00'}</div></div>
              <div className="abm-metric-card"><div className="metric-label">Daily Budget</div><div className="metric-value">${c.dailyBudget}</div></div>
            </div>
          )}

          {/* Steps detail */}
          <div className="abm-card">
            <h3>Campaign Steps</h3>
            <table className="abm-table">
              <thead><tr><th>#</th><th>Headline</th><th>Duration</th><th>Status</th><th>Started</th></tr></thead>
              <tbody>
                {campaignDetail.steps.map(step => (
                  <tr key={step.stepNumber}>
                    <td>{step.stepNumber}</td>
                    <td>{step.headline || step.blogPostTitle}</td>
                    <td>{step.duration}d</td>
                    <td>{statusBadge(step.status)}</td>
                    <td>{step.startedAt ? new Date(step.startedAt).toLocaleDateString() : '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Account engagement heat map */}
          {campaignDetail.accounts.length > 0 && (
            <div className="abm-card">
              <h3>Account Engagement</h3>
              {campaignDetail.accounts.map((acct, i) => {
                const heat = acct.engagementScore >= 70 ? 'hot' : acct.engagementScore >= 30 ? 'warm' : 'cold';
                return (
                  <div className="abm-heatmap-row" key={i}>
                    <span style={{ minWidth: 160, fontWeight: 500 }}>{acct.companyName || acct.companyUrn}</span>
                    <div style={{ flex: 1 }}>
                      <div className={`abm-heat-bar abm-heat-${heat}`} style={{ width: `${acct.engagementScore}%` }} />
                    </div>
                    <span style={{ minWidth: 40, textAlign: 'right', fontSize: 13, fontWeight: 600 }}>{acct.engagementScore}</span>
                    <span style={{ minWidth: 80, fontSize: 12, color: '#64748b' }}>{acct.impressions} imp / {acct.clicks} clk</span>
                  </div>
                );
              })}
            </div>
          )}

          {/* Event log */}
          {campaignDetail.events.length > 0 && (
            <div className="abm-card">
              <h3>Event Log</h3>
              {campaignDetail.events.map((evt, i) => (
                <div className="abm-event" key={i}>
                  <span className="abm-event-type">{evt.eventType}</span>
                  <span>{evt.fromStep ? `Step ${evt.fromStep} -> ${evt.toStep}` : ''}</span>
                  <span className="abm-event-time">{new Date(evt.timestamp).toLocaleString()}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      );
    }

    return (
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3 style={{ margin: 0 }}>ABM Campaigns</h3>
          <button className="abm-btn abm-btn-primary" onClick={() => setShowCreateCampaign(true)}>Create Campaign</button>
        </div>

        {showCreateCampaign && (
          <div className="abm-card">
            <h3>New ABM Campaign</h3>
            <div className="abm-form-row">
              <div className="abm-form-group">
                <label>Campaign Name</label>
                <input className="abm-input" value={campaignForm.name} onChange={e => setCampaignForm({ ...campaignForm, name: e.target.value })} placeholder="Q1 Enterprise QSR Outreach" />
              </div>
              <div className="abm-form-group">
                <label>Company List</label>
                <select className="abm-select" value={campaignForm.companyListId} onChange={e => setCampaignForm({ ...campaignForm, companyListId: e.target.value })}>
                  <option value="">Select a list...</option>
                  {lists.map(l => <option key={l.listId} value={l.listId}>{l.name} ({l.companyCount} companies)</option>)}
                </select>
              </div>
            </div>
            <div className="abm-form-row">
              <div className="abm-form-group">
                <label>Daily Budget ($)</label>
                <input className="abm-input" type="number" value={campaignForm.dailyBudget} onChange={e => setCampaignForm({ ...campaignForm, dailyBudget: Number(e.target.value) })} />
              </div>
              <div className="abm-form-group">
                <label>Job Title Filter (comma-separated)</label>
                <input className="abm-input" value={campaignForm.jobTitleFilter} onChange={e => setCampaignForm({ ...campaignForm, jobTitleFilter: e.target.value })} />
              </div>
            </div>

            {/* Add steps */}
            <div className="abm-form-group">
              <label>Campaign Steps</label>
              {campaignSteps.map((step, i) => (
                <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center' }}>
                  <span style={{ fontWeight: 600, minWidth: 20 }}>{i + 1}.</span>
                  <input className="abm-input" placeholder="Headline" value={step.headline} onChange={e => { const s = [...campaignSteps]; s[i] = { ...s[i], headline: e.target.value }; setCampaignSteps(s); }} style={{ flex: 2 }} />
                  <input className="abm-input" placeholder="Intro text" value={step.introText} onChange={e => { const s = [...campaignSteps]; s[i] = { ...s[i], introText: e.target.value }; setCampaignSteps(s); }} style={{ flex: 3 }} />
                  <input className="abm-input" type="number" placeholder="Days" value={step.duration} onChange={e => { const s = [...campaignSteps]; s[i] = { ...s[i], duration: Number(e.target.value) }; setCampaignSteps(s); }} style={{ width: 70 }} />
                  <button className="abm-btn abm-btn-danger" style={{ padding: '4px 8px' }} onClick={() => setCampaignSteps(campaignSteps.filter((_, j) => j !== i))}>X</button>
                </div>
              ))}
              <button className="abm-btn abm-btn-secondary" onClick={() => setCampaignSteps([...campaignSteps, { stepNumber: campaignSteps.length + 1, blogPostTitle: '', headline: '', introText: '', imageUrl: '', duration: 7, status: 'PENDING', startedAt: null, completedAt: null }])}>
                + Add Step
              </button>
            </div>

            <div className="abm-btn-group">
              <button className="abm-btn abm-btn-primary" onClick={handleCreateCampaign} disabled={!campaignForm.name || !campaignForm.companyListId || campaignSteps.length === 0 || actionLoading}>Create Campaign</button>
              <button className="abm-btn abm-btn-secondary" onClick={() => { setShowCreateCampaign(false); setCampaignSteps([]); }}>Cancel</button>
            </div>
          </div>
        )}

        {campaignsLoading ? (
          <div className="abm-loading">Loading campaigns...</div>
        ) : campaigns.length === 0 ? (
          <div className="abm-empty">No ABM campaigns yet. Create one or use the Sequence Wizard.</div>
        ) : (
          <table className="abm-table">
            <thead><tr><th>Name</th><th>Status</th><th>Progress</th><th>Budget</th><th>Created</th><th>Actions</th></tr></thead>
            <tbody>
              {campaigns.map(c => (
                <tr key={c.campaignId}>
                  <td><span style={{ cursor: 'pointer', color: '#2563eb' }} onClick={() => setSelectedCampaign(c.campaignId)}>{c.name}</span></td>
                  <td>{statusBadge(c.status)}</td>
                  <td>{c.currentStep}/{c.totalSteps} steps</td>
                  <td>${c.dailyBudget}/day</td>
                  <td>{new Date(c.createdAt).toLocaleDateString()}</td>
                  <td>
                    <div className="abm-btn-group" style={{ marginTop: 0 }}>
                      {c.status === 'DRAFT' && <button className="abm-btn abm-btn-success" style={{ padding: '4px 10px', fontSize: 12 }} onClick={() => handlePublishCampaign(c.campaignId)} disabled={actionLoading}>Publish</button>}
                      {c.status === 'ACTIVE' && <button className="abm-btn abm-btn-secondary" style={{ padding: '4px 10px', fontSize: 12 }} onClick={() => handleCampaignAction(c.campaignId, 'pause')} disabled={actionLoading}>Pause</button>}
                      {c.status === 'PAUSED' && <button className="abm-btn abm-btn-primary" style={{ padding: '4px 10px', fontSize: 12 }} onClick={() => handleCampaignAction(c.campaignId, 'resume')} disabled={actionLoading}>Resume</button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    );
  };

  // =============================================
  // RENDER: Sequence Wizard Tab
  // =============================================
  const renderWizardTab = () => {
    return (
      <div>
        <h3 style={{ margin: '0 0 16px' }}>AI Sequence Wizard</h3>

        <div className="abm-wizard-steps">
          {[1, 2, 3, 4, 5].map(s => (
            <span key={s} className={`abm-wizard-step ${wizardStep === s ? 'active' : wizardStep > s ? 'done' : ''}`}>
              {s === 1 ? 'Configure' : s === 2 ? 'Blog Posts' : s === 3 ? 'Generate' : s === 4 ? 'Review' : 'Assign'}
            </span>
          ))}
        </div>

        {/* Step 1: Configure */}
        {wizardStep === 1 && (
          <div className="abm-card">
            <h3>Step 1: Select Tier & Segment</h3>
            <div className="abm-form-row-3">
              <div className="abm-form-group">
                <label>Tier</label>
                <select className="abm-select" value={wizardConfig.tier} onChange={e => {
                  const tier = e.target.value;
                  const steps = tier === 'TIER_1' ? 6 : tier === 'TIER_2' ? 4 : 3;
                  setWizardConfig({ ...wizardConfig, tier, stepCount: steps });
                }}>
                  <option value="TIER_1">Tier 1 - Strategic (50+ locations, 6 steps)</option>
                  <option value="TIER_2">Tier 2 - Growth (10-50 locations, 4 steps)</option>
                  <option value="TIER_3">Tier 3 - Market (3-10 locations, 3 steps)</option>
                </select>
              </div>
              <div className="abm-form-group">
                <label>Segment</label>
                <select className="abm-select" value={wizardConfig.segment} onChange={e => setWizardConfig({ ...wizardConfig, segment: e.target.value })}>
                  <option value="QSR">QSR (Quick Service)</option>
                  <option value="FAST_CASUAL">Fast Casual</option>
                  <option value="FULL_SERVICE">Full Service</option>
                  <option value="PIZZA">Pizza</option>
                  <option value="COFFEE_BAKERY">Coffee & Bakery</option>
                </select>
              </div>
              <div className="abm-form-group">
                <label>Number of Steps</label>
                <input className="abm-input" type="number" min={2} max={8} value={wizardConfig.stepCount} onChange={e => setWizardConfig({ ...wizardConfig, stepCount: Number(e.target.value) })} />
              </div>
            </div>
            <div className="abm-btn-group">
              <button className="abm-btn abm-btn-primary" onClick={() => setWizardStep(2)}>Next</button>
            </div>
          </div>
        )}

        {/* Step 2: Blog posts */}
        {wizardStep === 2 && (
          <div className="abm-card">
            <h3>Step 2: Blog Posts (Optional)</h3>
            <p style={{ fontSize: 13, color: '#64748b', marginBottom: 12 }}>Add blog posts to reference in the sequence. Leave empty for generic content.</p>
            {wizardBlogPosts.map((bp, i) => (
              <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                <input className="abm-input" placeholder="Blog post title" value={bp.title} onChange={e => { const p = [...wizardBlogPosts]; p[i] = { ...p[i], title: e.target.value }; setWizardBlogPosts(p); }} style={{ flex: 2 }} />
                <input className="abm-input" placeholder="URL" value={bp.url} onChange={e => { const p = [...wizardBlogPosts]; p[i] = { ...p[i], url: e.target.value }; setWizardBlogPosts(p); }} style={{ flex: 2 }} />
                <select className="abm-select" value={bp.category} onChange={e => { const p = [...wizardBlogPosts]; p[i] = { ...p[i], category: e.target.value }; setWizardBlogPosts(p); }} style={{ width: 140 }}>
                  <option value="awareness">Awareness</option>
                  <option value="consideration">Consideration</option>
                  <option value="conversion">Conversion</option>
                </select>
                {wizardBlogPosts.length > 1 && (
                  <button className="abm-btn abm-btn-danger" style={{ padding: '4px 8px' }} onClick={() => setWizardBlogPosts(wizardBlogPosts.filter((_, j) => j !== i))}>X</button>
                )}
              </div>
            ))}
            <button className="abm-btn abm-btn-secondary" style={{ marginTop: 8 }} onClick={() => setWizardBlogPosts([...wizardBlogPosts, { title: '', url: '', category: 'awareness' }])}>+ Add Blog Post</button>
            <div className="abm-btn-group">
              <button className="abm-btn abm-btn-secondary" onClick={() => setWizardStep(1)}>Back</button>
              <button className="abm-btn abm-btn-primary" onClick={() => setWizardStep(3)}>Next</button>
            </div>
          </div>
        )}

        {/* Step 3: Generate */}
        {wizardStep === 3 && (
          <div className="abm-card" style={{ textAlign: 'center' }}>
            <h3>Step 3: Generate Sequence</h3>
            <p style={{ color: '#64748b', marginBottom: 20 }}>
              Ready to generate a {wizardConfig.stepCount}-step ABM sequence for {wizardConfig.tier.replace('_', ' ')} {wizardConfig.segment} accounts.
            </p>
            <div className="abm-btn-group" style={{ justifyContent: 'center' }}>
              <button className="abm-btn abm-btn-secondary" onClick={() => setWizardStep(2)}>Back</button>
              <button className="abm-btn abm-btn-primary" onClick={handleGenerateSequence} disabled={wizardLoading}>
                {wizardLoading ? 'Generating...' : 'Generate with AI'}
              </button>
            </div>
          </div>
        )}

        {/* Step 4: Review */}
        {wizardStep === 4 && (
          <div>
            <div className="abm-card">
              <h3>Step 4: Review Sequence</h3>
              <p style={{ fontSize: 13, color: '#64748b', marginBottom: 12 }}>Edit headlines and intro text as needed.</p>
            </div>
            <div className="abm-sequence-cards">
              {generatedSequence.map((step, i) => (
                <div className="abm-sequence-card" key={i}>
                  <div className="abm-sequence-num">{step.stepNumber || i + 1}</div>
                  <div className="abm-sequence-content">
                    <div className={`abm-sequence-phase ${(step.phase || step.category || 'awareness').toLowerCase()}`}>
                      {step.phase || step.category || 'AWARENESS'}
                    </div>
                    <input className="abm-input" value={step.headline} onChange={e => {
                      const s = [...generatedSequence]; s[i] = { ...s[i], headline: e.target.value }; setGeneratedSequence(s);
                    }} style={{ marginBottom: 8, fontWeight: 600 }} />
                    <textarea className="abm-textarea" value={step.introText} onChange={e => {
                      const s = [...generatedSequence]; s[i] = { ...s[i], introText: e.target.value }; setGeneratedSequence(s);
                    }} style={{ minHeight: 60 }} />
                    <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 4 }}>Duration: {step.duration} days</div>
                  </div>
                </div>
              ))}
            </div>
            <div className="abm-btn-group" style={{ marginTop: 16 }}>
              <button className="abm-btn abm-btn-secondary" onClick={() => setWizardStep(3)}>Regenerate</button>
              <button className="abm-btn abm-btn-primary" onClick={() => setWizardStep(5)}>Use This Sequence</button>
            </div>
          </div>
        )}

        {/* Step 5: Assign */}
        {wizardStep === 5 && (
          <div className="abm-card">
            <h3>Step 5: Create Campaign</h3>
            <div className="abm-form-row">
              <div className="abm-form-group">
                <label>Campaign Name</label>
                <input className="abm-input" value={wizardCampaignName} onChange={e => setWizardCampaignName(e.target.value)} placeholder="Q1 Enterprise QSR Campaign" />
              </div>
              <div className="abm-form-group">
                <label>Company List</label>
                <select className="abm-select" value={wizardListId} onChange={e => setWizardListId(e.target.value)}>
                  <option value="">Select a list...</option>
                  {lists.map(l => <option key={l.listId} value={l.listId}>{l.name} ({l.companyCount} companies)</option>)}
                </select>
              </div>
            </div>
            <div className="abm-form-group">
              <label>Daily Budget ($)</label>
              <input className="abm-input" type="number" value={wizardBudget} onChange={e => setWizardBudget(Number(e.target.value))} style={{ maxWidth: 200 }} />
            </div>
            <div className="abm-btn-group">
              <button className="abm-btn abm-btn-secondary" onClick={() => setWizardStep(4)}>Back</button>
              <button className="abm-btn abm-btn-primary" onClick={handleWizardCreateCampaign} disabled={!wizardCampaignName || !wizardListId || actionLoading}>
                Create as Draft
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  // =============================================
  // RENDER: Analytics Tab
  // =============================================
  const renderAnalyticsTab = () => {
    const detail = analyticsCampaignId && selectedCampaign === analyticsCampaignId ? campaignDetail : null;
    const m = detail?.metrics;

    return (
      <div>
        <h3 style={{ margin: '0 0 16px' }}>Campaign Analytics</h3>
        <div className="abm-form-group">
          <label>Select Campaign</label>
          <select className="abm-select" value={analyticsCampaignId} onChange={e => {
            setAnalyticsCampaignId(e.target.value);
            if (e.target.value) setSelectedCampaign(e.target.value);
          }} style={{ maxWidth: 400 }}>
            <option value="">Choose a campaign...</option>
            {campaigns.map(c => <option key={c.campaignId} value={c.campaignId}>{c.name} ({c.status})</option>)}
          </select>
        </div>

        {analyticsCampaignId && m && (
          <>
            <div className="abm-metrics-grid">
              <div className="abm-metric-card"><div className="metric-label">Impressions</div><div className="metric-value">{(m.totalImpressions || 0).toLocaleString()}</div></div>
              <div className="abm-metric-card"><div className="metric-label">Clicks</div><div className="metric-value">{(m.totalClicks || 0).toLocaleString()}</div></div>
              <div className="abm-metric-card"><div className="metric-label">CTR</div><div className="metric-value">{m.ctr || '0.00'}%</div></div>
              <div className="abm-metric-card"><div className="metric-label">Total Spend</div><div className="metric-value">${(m.totalSpend || 0).toFixed(2)}</div></div>
              <div className="abm-metric-card"><div className="metric-label">CPC</div><div className="metric-value">${m.cpc || '0.00'}</div></div>
              <div className="abm-metric-card"><div className="metric-label">Conversions</div><div className="metric-value">{m.totalConversions || 0}</div></div>
            </div>

            {/* Per-step breakdown */}
            {detail.stepMetrics.length > 0 && (
              <div className="abm-card">
                <h3>Per-Step Breakdown</h3>
                <table className="abm-table">
                  <thead><tr><th>Step</th><th>Impressions</th><th>Clicks</th><th>CTR</th><th>Spend</th><th>CPC</th></tr></thead>
                  <tbody>
                    {detail.stepMetrics.map((sm: any, i: number) => (
                      <tr key={i}>
                        <td>Step {sm.stepNumber}</td>
                        <td>{(sm.impressions || 0).toLocaleString()}</td>
                        <td>{sm.clicks || 0}</td>
                        <td>{sm.ctr || '0.00'}%</td>
                        <td>${(sm.spend || 0).toFixed(2)}</td>
                        <td>${sm.cpc || '0.00'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Account heat map */}
            {detail.accounts.length > 0 && (
              <div className="abm-card">
                <h3>Account Heat Map</h3>
                <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 12 }}>
                  Sorted by engagement score. Hot (&gt;70) / Warm (30-70) / Cold (&lt;30)
                </div>
                {detail.accounts.map((acct, i) => {
                  const heat = acct.engagementScore >= 70 ? 'hot' : acct.engagementScore >= 30 ? 'warm' : 'cold';
                  return (
                    <div className="abm-heatmap-row" key={i}>
                      <span style={{ minWidth: 180, fontWeight: 500, fontSize: 13 }}>{acct.companyName || acct.companyUrn}</span>
                      <div style={{ flex: 1, background: '#f1f5f9', borderRadius: 4, height: 8, overflow: 'hidden' }}>
                        <div className={`abm-heat-bar abm-heat-${heat}`} style={{ width: `${acct.engagementScore}%` }} />
                      </div>
                      <span style={{ minWidth: 40, textAlign: 'right', fontSize: 13, fontWeight: 700, color: heat === 'hot' ? '#dc2626' : heat === 'warm' ? '#f59e0b' : '#94a3b8' }}>{acct.engagementScore}</span>
                      <span style={{ minWidth: 120, fontSize: 12, color: '#64748b', textAlign: 'right' }}>{acct.impressions} imp / {acct.clicks} clk</span>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}

        {analyticsCampaignId && !m && (
          <div className="abm-empty">Loading metrics...</div>
        )}

        {!analyticsCampaignId && (
          <div className="abm-empty">Select a campaign to view analytics.</div>
        )}
      </div>
    );
  };

  return (
    <div className="abm-dashboard">
      <div className="abm-tabs">
        <button className={`abm-tab ${subTab === 'lists' ? 'active' : ''}`} onClick={() => { setSubTab('lists'); setSelectedList(null); }}>Account Lists</button>
        <button className={`abm-tab ${subTab === 'campaigns' ? 'active' : ''}`} onClick={() => { setSubTab('campaigns'); setSelectedCampaign(null); }}>Campaigns</button>
        <button className={`abm-tab ${subTab === 'wizard' ? 'active' : ''}`} onClick={() => setSubTab('wizard')}>Sequence Wizard</button>
        <button className={`abm-tab ${subTab === 'analytics' ? 'active' : ''}`} onClick={() => setSubTab('analytics')}>Analytics</button>
      </div>

      {subTab === 'lists' && renderListsTab()}
      {subTab === 'campaigns' && renderCampaignsTab()}
      {subTab === 'wizard' && renderWizardTab()}
      {subTab === 'analytics' && renderAnalyticsTab()}
    </div>
  );
};

export default ABMDashboard;
